import type { VRCertificateType } from "@constants/certificates";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

interface UploadParams {
  file: File;
  certificateType: VRCertificateType;
  idx?: number;
}

export const useUploadCertificate = (
  onSuccess?: (data: {
    documentUrl: string;
    certificateType: VRCertificateType;
    documentName: string;
  }) => void,
  onError?: (certificateType: VRCertificateType) => void,
) => {
  const service = "vessel-bff";
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ file, certificateType, idx }: UploadParams) =>
      runTransaction(
        {
          module: "vessel_registration",
          screen: "VesselCertificates",
          action: "upload_vessel_certificate",
          entityType: "vessel_certificate",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.VESSEL_REGISTRATION,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };
          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [result] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType,
          });
          const documentUrl = result?.documentUrl ?? blobUrl;
          return { documentUrl, certificateType, idx, documentName: file.name };
        },
      ),
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
      qc.invalidateQueries({ queryKey: ["certificates", service] });
    },
    onError: (_, variables) => {
      if (onError) onError(variables.certificateType);
    },
  });
};
